import { Badge, Button, Card, Group, Stack, Text, Title } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { modals } from "@mantine/modals";
import { IconCalendar, IconMapPin, IconUsers } from "@tabler/icons-react";
import { format, parseISO, startOfDay } from "date-fns";
import { useState } from "react";
import type { GroupClassSearchOutput } from "@/api-types";
import { Portrait } from "@/components/Portrait";
import { UniversalRoutes, useNavigator } from "@/routers/routes";
import { specialtyOptionsMap } from "@/pages/trainer/profile/common";

interface GroupClassCardProps {
  groupClass: GroupClassSearchOutput;
  showJoinButton?: boolean;
}

export const GroupClassCard = ({
  groupClass,
  showJoinButton = false,
}: GroupClassCardProps) => {
  const { navigator } = useNavigator();
  const [showAllSessions, setShowAllSessions] = useState(false);

  const sessions = groupClass.classType.scheduledSessions || [];
  const hasMoreSessions = sessions.length > 4;
  const displaySessions = showAllSessions
    ? sessions
    : sessions.slice(0, sessions.length > 4 ? 3 : sessions.length);

  const showSessionsModal = () => {
    const sessionDates = sessions.map((sessionDate) =>
      startOfDay(parseISO(sessionDate)),
    );

    modals.open({
      title: "Session Dates",
      children: (
        <Stack align="center">
          <DatePicker type="multiple" value={sessionDates} size="md" />
        </Stack>
      ),
    });
  };

  return (
    <Card p="md" withBorder>
      <Group align="flex-start" gap="md" style={{ width: "100%" }}>
        {/* Left Column */}
        <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
          <div>
            {groupClass.classType.name ? (
              <>
                <Title order={3}>{groupClass.classType.name}</Title>
                {groupClass.classType.description && (
                  <Text size="sm" c="dimmed" mt="xs">
                    {groupClass.classType.description}
                  </Text>
                )}
                <Text size="sm" fw={500} mt="sm">
                  Group "{groupClass.group.name}"
                </Text>
                {groupClass.group.description && (
                  <Text size="sm" c="dimmed" mt="xs">
                    {groupClass.group.description}
                  </Text>
                )}
              </>
            ) : (
              <>
                <Title order={3}>Group "{groupClass.group.name}"</Title>
                {groupClass.group.description && (
                  <Text size="sm" c="dimmed" mt="xs">
                    {groupClass.group.description}
                  </Text>
                )}
              </>
            )}
          </div>

          <Group gap="md">
            <Portrait profile={groupClass.trainer} size="md" who="person" />
            <div style={{ flex: 1 }}>
              <Text fw={500}>
                {groupClass.trainer.firstName} {groupClass.trainer.lastName}
              </Text>
              <Group gap="xs">
                <IconMapPin size={14} color="gray" />
                <Text size="sm" c="dimmed">
                  {groupClass.trainer.locality}
                </Text>
              </Group>
            </div>
          </Group>

          {groupClass.classType.included &&
            groupClass.classType.included.length > 0 && (
              <div>
                <Text size="sm" fw={500} mb="xs">
                  What's included:
                </Text>
                <Text size="sm" c="dimmed">
                  {groupClass.classType.included.join(", ")}
                </Text>
              </div>
            )}

          {(groupClass.classType.themes ?? []).length > 0 && (
            <Group gap="xs">
              {(groupClass.classType.themes ?? []).map((theme: string) => (
                <Badge key={theme} size="sm" variant="light" color="green">
                  {specialtyOptionsMap[theme] ?? theme}
                </Badge>
              ))}
            </Group>
          )}
        </Stack>

        {/* Right Column */}
        <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs" align="center">
            <IconUsers size={16} color="gray" />
            <Text size="sm" c="dimmed">
              {groupClass.group.spotsLeft} spots left
            </Text>
            {groupClass.group.spotsLeft === 0 && (
              <Badge size="xs" color="orange">
                Full
              </Badge>
            )}
          </Group>

          {groupClass.classType.totalPrice &&
          groupClass.classType.totalPrice > 0 ? (
            <Text size="sm" fw={500} c="green">
              €{(groupClass.classType.totalPrice / 100).toFixed(2)} total
            </Text>
          ) : (
            <Text size="sm" fw={500} c="dimmed">
              Custom price
            </Text>
          )}

          {sessions.length > 0 && (
            <Stack gap="xs">
              <Group gap="xs">
                <IconCalendar size={16} color="gray" />
                <Text size="sm" fw={500}>
                  {groupClass.classType.sessionCount} sessions:
                </Text>
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={showSessionsModal}
                  style={{ padding: 0, height: "auto" }}
                >
                  view calendar
                </Button>
              </Group>
              <Stack gap="2px">
                {displaySessions.map((sessionDate) => (
                  <Text key={sessionDate} size="xs" c="dimmed">
                    {format(parseISO(sessionDate), "EEE, MMM d 'at' h:mm a")}
                  </Text>
                ))}
                {hasMoreSessions && !showAllSessions && (
                  <Button
                    variant="subtle"
                    size="xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAllSessions(true);
                    }}
                    style={{
                      alignSelf: "flex-start",
                      padding: 0,
                      height: "auto",
                    }}
                  >
                    See all
                  </Button>
                )}
              </Stack>
            </Stack>
          )}
        </Stack>
      </Group>

      <Stack gap="sm" mt="md">
        <Button
          fullWidth
          variant="outline"
          size="sm"
          onClick={navigator({
            path: UniversalRoutes.TRAINER,
            params: { trainerId: groupClass.trainer.id },
          })}
        >
          View Trainer Page
        </Button>
        {showJoinButton && (
          <Button
            fullWidth
            onClick={navigator({
              path: UniversalRoutes.GROUPS.JOIN,
              params: { groupId: groupClass.group.id },
            })}
          >
            Click to join
          </Button>
        )}
      </Stack>
    </Card>
  );
};
